// slothfulchat-web: pure aggregation of the chatmail relays a user's contacts
// are on, for the "Relays your contacts use" section of the relay picker opened
// from the Transports dialog.
//
// Kept free of browser/runtime imports (only the pure host validator from
// relayDirectoryParse) so the unit test (src/tests/contactRelays.test.ts) can
// load it under plain node — the getContactIds/getContactsByIds RPC glue lives
// in Transports/index.tsx.

import { normalizeRelayHost } from '../../screens/WelcomeScreen/relayDirectoryParse.js'

/**
 * Group contact email addresses by their domain (the part after the last `@`,
 * lowercased and validated as a bare hostname), counting how many contacts use
 * each, dropping any host in `exclude`. Malformed addresses (no `@`, empty
 * local part, non-hostname domain) are skipped rather than counted.
 *
 * Sorted most-used first, ties broken alphabetically — the section's initial
 * order before the user asks for a reachability measurement.
 *
 * ponytail: a contact's mail domain is not necessarily a chatmail relay; adding
 * a non-chatmail domain fails loudly through useAddTransportDialog's existing
 * error alert. That is the accepted design — we don't pre-validate chatmail-ness
 * here (would need a network round trip per host just to hide a row).
 */
export function contactRelayCandidates(
  addresses: string[],
  exclude: Set<string>
): { host: string; contacts: number }[] {
  const counts = new Map<string, number>()
  for (const addr of addresses) {
    if (typeof addr !== 'string') continue
    const at = addr.lastIndexOf('@')
    if (at < 1) continue // no '@', or empty local part
    const host = normalizeRelayHost(addr.slice(at + 1))
    if (!host || exclude.has(host)) continue
    counts.set(host, (counts.get(host) ?? 0) + 1)
  }
  return Array.from(counts, ([host, contacts]) => ({ host, contacts })).sort(
    (a, b) => b.contacts - a.contacts || a.host.localeCompare(b.host)
  )
}
